"use client";

import type {
  HTMLAttributes,
  ReactNode,
  ElementType,
  MouseEvent,
  FC,
  Ref,
} from "react";
import { isString, mergeClasses } from "@huub-web/utils";

import { Card, type CardProps } from "../Card";
import type { UserPromptRecord } from "../../types";
import { Tooltip } from "../Tooltip";
import { Button, type ButtonProps } from "../Button";
import { Icon } from "../Icon";
import { useHover } from "../../hooks/useHover";
import { useTracker } from "../../hooks";
import { Text } from "../Text";
import { Spacing } from "../Spacing";
import type { TrackingProps } from "../../props";
import { focusVisibleInset } from "../../theme/css/focusVisible.css";

import {
  buttonStyle,
  descriptionStyle,
  flowCardStyle,
  flowCardContentStyle,
} from "./FlowCard.css";

export type FlowCardProps = Omit<
  HTMLAttributes<HTMLDivElement | HTMLButtonElement>,
  "title"
> &
  Pick<CardProps, "columns" | "applyMotion" | "border" | "leading"> &
  TrackingProps & {
    as?: ElementType;
    title?: ReactNode;
    subtitle?: ReactNode;
    disabled?: boolean;
    active?: boolean;
    width?: "default" | "stretch";
    variant?: CardProps["variant"];
    trailing?: ReactNode;
    href?: string;
    description?: ReactNode;
    tooltip?: ReactNode;
    value?: ReactNode;
    prompt?: UserPromptRecord;
    button?: string;
    buttonTrailing?: ReactNode;
    onPrompt?: () => void;
    buttonSize?: ButtonProps["size"];
    /**
     * @deprecated No longer rendered
     */
    icon?: string | ReactNode;
    /**
     * @deprecated No longer rendered
     */
    step?: number | string;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement | HTMLButtonElement | HTMLAnchorElement>;
  };

/**
 * A card component that displays a call-to-action with optional description and button.
 * @deprecated
 */
export const FlowCard: FC<FlowCardProps> = ({
  as,
  title,
  subtitle,
  description,
  disabled,
  active,
  width = "default",
  variant,
  border = "default",
  columns,
  tracking,
  trailing,
  prompt,
  value = "Vraag het aan Huub",
  tooltip,
  button,
  buttonTrailing,
  buttonSize = "s",
  onPrompt,
  onClick,
  className,
  children,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  icon: _icon,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  step: _step,
  ref,
  ...props
}) => {
  const { handleEnter, handleLeave, hasHover } = useHover({ disabled });
  const { track } = useTracker();
  const isInteractive = !!onClick;

  const handleClick = (e: MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
    if (prompt) {
      e.preventDefault();
      onPrompt?.();
    }
    onClick?.(e);
    if (tracking) {
      track("interaction", {
        category: "button",
        action: "click",
        label: tracking,
        properties: {
          buttonId: tracking,
          cardTitle: typeof title === "string" ? title : undefined,
          buttonType: "card",
        },
      });
    }
  };

  return (
    <Card
      ref={ref as Ref<HTMLDivElement>}
      as={as ?? (isInteractive ? "button" : "div")}
      className={mergeClasses(
        flowCardStyle({ disabled, width }),
        focusVisibleInset,
        className,
      )}
      contentClassName={flowCardContentStyle()}
      border={border}
      onClick={isInteractive ? handleClick : undefined}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      variant={variant}
      columns={columns}
      overflowHidden
      {...(active && { "data-active": true })}
      {...(disabled && { disabled })}
      data-tracking={tracking}
      {...props}
    >
      {trailing}

      <Text variant="displayS" clamp={2} bold>
        {title}
      </Text>

      {subtitle && (
        <Text variant="bodyS" color="currentColor50" clamp={3}>
          {subtitle}
        </Text>
      )}

      {description && (
        <>
          {isString(description) ? (
            <Text
              color={variant === "gradient" ? undefined : "secondary"}
              className={descriptionStyle}
              markdown
            >
              {description}
            </Text>
          ) : (
            description
          )}
          {!button && !prompt && <Spacing />}
        </>
      )}

      {children}

      {prompt && (
        <Tooltip value={tooltip}>
          <Button
            as="div"
            className={buttonStyle}
            trailing={<Icon icon="aiFilled" size="s" />}
            variant={variant === "gradient" ? "tertiary" : "primary"}
            size="s"
            disabled={disabled}
            active={hasHover}
          >
            {value}
          </Button>
        </Tooltip>
      )}
      {button && !prompt && (
        <Button
          as="div"
          className={buttonStyle}
          variant={variant === "gradient" ? "tertiary" : "primary"}
          size={variant === "gradient" ? "s" : buttonSize}
          disabled={disabled}
          active={hasHover}
          trailing={
            variant === "gradient" ? (
              <Icon icon="arrowRight" size="s" />
            ) : (
              buttonTrailing
            )
          }
        >
          {button}
        </Button>
      )}
    </Card>
  );
};
